import { useState } from "react";
import { Share2 } from "lucide-react";
import { resolveCoverUrl } from "@/lib/api";
import { pickSpineColor } from "@/lib/constants";
import { toast } from "sonner";

// Deterministic small lean angle from book id
function leanAngle(seed) {
  if (!seed) return 0;
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  // 70% upright, 30% leaning ±2-4deg
  const bucket = hash % 10;
  if (bucket < 7) return 0;
  if (bucket === 7) return -3;
  if (bucket === 8) return 2;
  return -2;
}

export default function BookSpine({ book, onOpenDetail }) {
  const [open, setOpen] = useState(false);
  const color = pickSpineColor(book.title + book.author_name);
  const coverUrl = resolveCoverUrl(book.cover_image_url);
  const lean = leanAngle(book.id);

  const handleShare = async (e) => {
    e.stopPropagation();
    const shareData = {
      title: book.title,
      text: `${book.title} by ${book.author_name} — on The Bookshelf`,
      url: book.purchase_url,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(book.purchase_url);
        toast.success("Link copied to clipboard");
      }
    } catch {
      /* user canceled */
    }
  };

  return (
    <div
      className="relative group"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label={`${book.title} by ${book.author_name} — click to view details`}
        onClick={() => onOpenDetail?.(book)}
        className="book-spine relative w-[130px] h-[195px] md:w-[160px] md:h-[235px] flex-shrink-0 rounded-[3px] overflow-hidden border-l-[3px] border-black/25 border-r border-black/15"
        style={{
          backgroundColor: color,
          backgroundImage: coverUrl ? `url(${coverUrl})` : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",
          "--lean": `${lean}deg`,
        }}
        data-testid={`book-spine-${book.id}`}
      >
        {!coverUrl && (
          <div className="absolute inset-0 flex items-center justify-center p-1">
            <span
              className="writing-vertical-rl text-xs md:text-sm font-bold text-white line-clamp-3 text-center leading-tight px-1"
              style={{ textShadow: "0 1px 2px rgba(0,0,0,0.55)" }}
            >
              {book.title}
            </span>
          </div>
        )}
        {book.is_new && (
          <span
            className="absolute -top-2 -right-2 bg-[#2B328C] text-white text-[10px] uppercase font-bold px-2 py-0.5 rounded shadow-md tracking-wider border border-[#1F2470] z-10"
            data-testid={`new-badge-${book.id}`}
          >
            New
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={`Details for ${book.title}`}
          className="absolute left-1/2 -translate-x-1/2 bottom-[115%] w-[280px] md:w-[320px] z-50 paper-card rounded-lg shadow-[0_20px_45px_-10px_rgba(14,35,46,0.35)] animate-fade-up"
          data-testid={`book-popover-${book.id}`}
        >
          <div className="p-4">
            <div className="flex gap-3">
              <div
                className="w-20 h-28 flex-shrink-0 rounded-sm overflow-hidden shadow-md"
                style={{
                  backgroundColor: color,
                  backgroundImage: coverUrl ? `url(${coverUrl})` : undefined,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              >
                {!coverUrl && (
                  <div className="w-full h-full flex items-center justify-center p-1">
                    <span className="text-[9px] font-bold text-white text-center leading-tight">
                      {book.title}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-lg font-extrabold leading-tight text-[#0E232E]">
                  {book.title}
                </h3>
                <p className="text-xs text-[#2F2E2E] mt-0.5">by {book.author_name}</p>
                <span className="inline-block mt-2 text-[9px] font-semibold uppercase tracking-wider bg-[#7EC8E3]/20 text-[#0E232E] px-2 py-0.5 rounded">
                  {book.age_group_label}
                </span>
              </div>
            </div>

            <p className="mt-3 text-[13px] text-[#2F2E2E] leading-snug line-clamp-5">
              {book.description}
            </p>

            <div className="mt-4 flex items-center gap-2">
              <a
                href={book.purchase_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex-1 btn-primary text-center py-2 px-3 rounded-md text-sm"
                data-testid={`buy-now-${book.id}`}
              >
                Get this book
              </a>
              <button
                type="button"
                onClick={handleShare}
                aria-label="Share this book"
                className="p-2 rounded-md border border-[#CFD6DC] text-[#0E232E] hover:bg-[#0E232E] hover:text-white transition-colors"
                data-testid={`share-btn-${book.id}`}
              >
                <Share2 size={16} />
              </button>
            </div>
            {book.author_link && (
              <a
                href={book.author_link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="block mt-2 text-[11px] text-[#2B328C] underline"
              >
                Author's website
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
